<template>
  <div class="tc-verify-day-group" :data-testid="`verify-day-group-${group.dayIndex}`">
    <div class="tc-verify-day-group-header">
      <span class="tc-verify-day-group-label">{{ group.label }}</span>
      <span class="tc-verify-day-group-total">{{ group.totalHours.toFixed(2) }}h total</span>
    </div>

    <!-- Already-in-VP sub-block: bordered, subtly shaded, visually read-only. -->
    <div
      v-if="group.vpRows.length > 0"
      class="tc-verify-vp-block"
      data-testid="verify-vp-block"
    >
      <div class="tc-verify-vp-block-label">Already in Vantagepoint</div>
      <VerifyVpRow
        v-for="vr in group.vpRows"
        :key="`vp-${vr.wbs1}-${vr.dayIndex}`"
        :entry="vr"
      />
    </div>

    <!-- Draft rows to be written — normal surface, brand edge + New chip. -->
    <div v-if="group.draftRows.length > 0" class="tc-verify-draft-block">
      <VerifyRow
        v-for="draft in group.draftRows"
        :key="draft.eventId"
        :draft="draft"
        :period-start="periodStart"
        :error="rowErrors[draft.eventId] ?? null"
        :vp-status="cellVpStatus(draft)"
        :conflict-vp-hours="conflictVpHours(draft)"
        :comment-required="commentRequired"
        :hours-increment="hoursIncrement"
        :show-labor-code="showLaborCode"
        :can-edit-labor-code="canEditLaborCode"
        :labor-codes="laborCodes"
        :default-labor-code="defaultLaborCode"
        @update="(id, patch) => emit('update', id, patch)"
        @remove="(id) => emit('remove', id)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import VerifyVpRow, { type VpDayEntry } from './VerifyVpRow.vue';
import VerifyRow from './VerifyRow.vue';
import type { TriageDraft } from '../stores/triage';
import type { LaborCodeItem } from '../api/timesheetSettings';

export interface DayGroup {
  dayIndex: number;
  label: string;
  totalHours: number;
  vpRows: VpDayEntry[];
  draftRows: TriageDraft[];
  hasConflict: boolean;
}

const props = defineProps<{
  group: DayGroup;
  periodStart: string;
  rowErrors: Record<string, string>;
  commentRequired?: boolean;
  /** Hours step from tenant settings. */
  hoursIncrement?: number;
  /** When true, show the labor code selector on each draft row. */
  showLaborCode?: boolean;
  /** When false, the labor code is locked to the default (VP ChangeDefaultLC !== 'Y'). */
  canEditLaborCode?: boolean;
  /** Available labor codes for this tenant. */
  laborCodes?: LaborCodeItem[];
  /** Tenant default labor code. */
  defaultLaborCode?: string;
  /** Reconcile state resolver for a draft cell vs live VP. */
  cellVpStatus: (cell: TriageDraft) => 'new' | 'in-vp' | 'overlap';
  /** Existing VP hours at a draft cell's (project, day) — surfaced on the conflict chip. */
  conflictVpHours: (cell: TriageDraft) => number;
}>();

const emit = defineEmits<{
  update: [eventId: string, patch: { hours: number; comment: string; laborCode?: string }];
  remove: [eventId: string];
}>();

// Keep props referenced for template (script-setup exposes them implicitly).
void props;
</script>
